import {
  Users,
  Clock,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Minus,
  Trophy,
} from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  useTeamEfficiency,
  formatPercent,
  getEfficiencyTrendDisplayName,
  getEfficiencyTrendColor,
  type AnalyticsFilters,
} from '@/hooks'
import { cn } from '@/lib/utils'

interface TeamEfficiencyChartProps {
  filters?: AnalyticsFilters
  className?: string
}

/**
 * 团队效率图表组件
 * Requirements: 5.1, 5.3, 5.4
 */
export function TeamEfficiencyChart({ filters = {}, className }: TeamEfficiencyChartProps) {
  const { data: teams, isLoading, error } = useTeamEfficiency(filters)

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            团队效率排名
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Clock className="h-5 w-5 animate-spin mr-2" />
            加载中...
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            团队效率排名
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-destructive">
            <AlertCircle className="h-5 w-5 mr-2" />
            加载失败
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!teams || teams.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            团队效率排名
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            暂无数据
          </div>
        </CardContent>
      </Card>
    )
  }

  const TrendIcon = ({ trend }: { trend: 'improving' | 'stable' | 'declining' }) => {
    switch (trend) {
      case 'improving':
        return <TrendingUp className="h-4 w-4" />
      case 'declining':
        return <TrendingDown className="h-4 w-4" />
      default:
        return <Minus className="h-4 w-4" />
    }
  }

  const getRankBadge = (rank: number) => {
    if (rank === 1) {return { icon: Trophy, color: 'bg-yellow-100 text-yellow-800' }}
    if (rank === 2) {return { icon: null, color: 'bg-gray-100 text-gray-800' }}
    if (rank === 3) {return { icon: null, color: 'bg-orange-100 text-orange-800' }}
    return { icon: null, color: 'bg-muted text-muted-foreground' }
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          团队效率排名
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {teams.map((team) => {
            const rankBadge = getRankBadge(team.rank)
            return (
              <div
                key={team.teamId}
                className={cn(
                  'p-4 rounded-lg border',
                  team.rank === 1 && 'border-yellow-200 bg-yellow-50/50 dark:bg-yellow-950/20'
                )}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold',
                        rankBadge.color
                      )}
                    >
                      {rankBadge.icon ? (
                        <rankBadge.icon className="h-4 w-4" />
                      ) : (
                        team.rank
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium">{team.teamName}</h4>
                      <p className="text-sm text-muted-foreground">
                        {team.taskCount} 个任务
                      </p>
                    </div>
                  </div>

                  <div className={cn('flex items-center gap-1', getEfficiencyTrendColor(team.trend))}>
                    <TrendIcon trend={team.trend} />
                    <span className="text-sm">{getEfficiencyTrendDisplayName(team.trend)}</span>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">平均完成时间</span>
                    <p className="font-medium">{team.averageCompletionDays.toFixed(1)} 天</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">回退率</span>
                    <p
                      className={cn(
                        'font-medium',
                        team.rollbackRate > 0.2 ? 'text-red-600' : ''
                      )}
                    >
                      {formatPercent(team.rollbackRate)}
                    </p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
